import { useRef } from "react";
import clsx from "clsx";

import { Tooltip } from "./Tooltip";

/** Tailwind needs the literal class in the source, so these can't be built
 *  from the `lines` number. */
const CLAMP_CLASS: Record<number, string> = {
  1: "truncate",
  2: "line-clamp-2",
  3: "line-clamp-3",
};

/**
 * Text that clips with an ellipsis when it doesn't fit and reveals the whole
 * value in a tooltip — but only when it is actually clipped, so a short value
 * doesn't sprout a tooltip repeating what is already on screen.
 *
 * The overflow check runs on hover rather than at render: it needs the
 * laid-out width, which isn't known while rendering, and the available width
 * changes with the viewport and with table column resizing — so a width
 * measured once would go stale. That is what Tooltip's `getLabel` is for.
 *
 * The Tooltip wrapper is `display: contents`, so this renders exactly the one
 * span it always did — dropping it into a table cell or a flex row can't
 * change how the text lays out.
 */
export function TruncatedText({
  text,
  className,
  lines = 1,
}: {
  text: string;
  className?: string;
  /** 1 clips to a single line; 2 or 3 clamp to that many lines. Use more than
   *  one where the values share a long prefix and only differ at the end. */
  lines?: 1 | 2 | 3;
}) {
  const ref = useRef<HTMLSpanElement>(null);

  return (
    <Tooltip
      display="contents"
      getLabel={() => {
        const el = ref.current;
        if (!el) return undefined;
        const clipped =
          lines === 1
            ? el.scrollWidth > el.clientWidth
            : el.scrollHeight > el.clientHeight;
        return clipped ? text : undefined;
      }}
    >
      {/* `block` only for the single-line case: line-clamp needs its own
          display (-webkit-box), and a `block` alongside it won the cascade,
          so lines={2} rendered every line and never clipped. */}
      <span
        ref={ref}
        className={clsx(lines === 1 && "block", CLAMP_CLASS[lines], className)}
      >
        {text}
      </span>
    </Tooltip>
  );
}
